/**
 * Rango de un monto en pesos: el total de los pedidos en el backoffice y el
 * precio en los resultados de busqueda. Se aplica con Enter o al salir de
 * los dos campos, no a cada tecla: pediria la tabla por cada digito. Sin boton
 * visible, para que la barra de filtros quepa en una linea; el de enviar queda
 * para lector de pantalla (y es el que habilita el Enter con dos campos).
 */
import { useEffect, useId, useState } from 'react';
import type { FocusEvent, FormEvent } from 'react';

export interface FiltroMontoProps {
  /** Que se filtra, con mayuscula: "Total", "Precio". */
  que: string;
  minimo: number | undefined;
  maximo: number | undefined;
  /** Textos de ejemplo de los campos; por defecto "Desde" y "Hasta". */
  marcadores?: { desde: string; hasta: string };
  onCambiar(rango: { minimo: number | undefined; maximo: number | undefined }): void;
}

/** Acepta `15.000` o `$15000`: se escribe como se lee un monto en Chile. */
function montoDe(texto: string): number | undefined {
  const digitos = texto.replace(/\D/g, '');
  return digitos.length > 0 ? Number(digitos) : undefined;
}

export function FiltroMonto({
  que,
  minimo,
  maximo,
  marcadores = { desde: 'Desde', hasta: 'Hasta' },
  onCambiar,
}: FiltroMontoProps) {
  const idEtiqueta = useId();
  const [desde, setDesde] = useState(minimo?.toString() ?? '');
  const [hasta, setHasta] = useState(maximo?.toString() ?? '');

  // Si el filtro cambio desde afuera (un chip quitado, "Limpiar todo"), los
  // campos muestran lo vigente y no lo que quedo escrito.
  useEffect(() => {
    setDesde(minimo?.toString() ?? '');
    setHasta(maximo?.toString() ?? '');
  }, [minimo, maximo]);

  function aplicar(): void {
    const menor = montoDe(desde);
    const mayor = montoDe(hasta);
    // Sin cambios no se pide la tabla otra vez (salir del campo tambien aplica).
    if (menor === minimo && mayor === maximo) return;
    // Escrito al reves se entiende igual: se dan vuelta los extremos.
    if (menor !== undefined && mayor !== undefined && menor > mayor) {
      onCambiar({ minimo: mayor, maximo: menor });
    } else {
      onCambiar({ minimo: menor, maximo: mayor });
    }
  }

  function enviar(evento: FormEvent<HTMLFormElement>): void {
    evento.preventDefault();
    aplicar();
  }

  // Pasar de "desde" a "hasta" no aplica: solo salir del rango entero.
  function salir(evento: FocusEvent<HTMLFormElement>): void {
    const destino = evento.relatedTarget;
    if (destino instanceof Node && evento.currentTarget.contains(destino)) return;
    aplicar();
  }

  const activo = minimo !== undefined || maximo !== undefined;
  const claseEntrada = activo ? 'entrada entrada-numero activo' : 'entrada entrada-numero';

  return (
    <div className="filtro filtro-stock">
      <span className="filtro-et" id={idEtiqueta}>
        {que} ($)
      </span>
      <form className="rango-stock" aria-labelledby={idEtiqueta} onSubmit={enviar} onBlur={salir}>
        <label>
          <span className="solo-lectores">{que} desde</span>
          <input
            type="text"
            inputMode="numeric"
            className={claseEntrada}
            placeholder={marcadores.desde}
            value={desde}
            onChange={(evento) => {
              setDesde(evento.target.value);
            }}
          />
        </label>
        <span aria-hidden="true">a</span>
        <label>
          <span className="solo-lectores">{que} hasta</span>
          <input
            type="text"
            inputMode="numeric"
            className={claseEntrada}
            placeholder={marcadores.hasta}
            value={hasta}
            onChange={(evento) => {
              setHasta(evento.target.value);
            }}
          />
        </label>
        <button type="submit" className="solo-lectores">
          Aplicar rango de {que.toLowerCase()}
        </button>
      </form>
    </div>
  );
}
